import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getVisibleModules, isPathActive } from "../../lib/navModules";
import { ChevronDown, LayoutGrid, Boxes, X } from "lucide-react";
import { cn } from "../../lib/utils";
import api from "../../lib/api";

export default function MobileNav({ onClose }) {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [enabledModules, setEnabledModules] = useState(null);
  const [customModules, setCustomModules] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/modules/enabled").then(res => setEnabledModules(res.data.enabled || null)).catch(() => {});
    api.get("/module-builder/modules").then(res => setCustomModules((res.data || []).filter(m => m.enabled))).catch(() => {});
  }, []);

  const modules = useMemo(
    () => getVisibleModules(enabledModules, hasPermission, user),
    [enabledModules, hasPermission, user]
  );

  const go = (path) => { navigate(path); onClose?.(); };

  return (
    <div className="h-full flex flex-col bg-card" data-testid="mobile-nav">
      <div className="h-14 px-4 flex items-center justify-between border-b shrink-0">
        <button onClick={() => go("/")} className="flex items-center gap-2 text-sm font-semibold" data-testid="mobile-apps-link">
          <LayoutGrid className="w-4 h-4" /> All Apps
        </button>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted" data-testid="mobile-nav-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2">
        {modules.map(mod => {
          const MIcon = mod.icon;
          const isOpen = expanded === mod.key;
          const items = mod.visibleSections.flatMap(s => s.items);
          const modActive = items.some(it => isPathActive(location.pathname, it.path));
          return (
            <div key={mod.key}>
              <button onClick={() => setExpanded(isOpen ? null : mod.key)}
                data-testid={`mobile-module-${mod.key}`}
                className={cn("flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium",
                  modActive ? "bg-muted" : "hover:bg-muted/50")}>
                <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: mod.color }}>
                  <MIcon className="w-4 h-4 text-white" strokeWidth={1.8} />
                </span>
                <span className="flex-1 text-left">{mod.label}</span>
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isOpen ? "rotate-0" : "-rotate-90")} />
              </button>
              {isOpen && (
                <div className="ml-6 pl-3 border-l space-y-0.5 my-1">
                  {items.map(it => {
                    const IIcon = it.icon;
                    const active = isPathActive(location.pathname, it.path);
                    return (
                      <button key={it.path} onClick={() => go(it.path)}
                        className={cn("flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[13px]",
                          active ? "bg-primary/10 text-primary font-semibold" : "text-foreground/70 hover:bg-muted/50")}>
                        <IIcon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                        {it.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {customModules.length > 0 && (
          <div className="mt-2 pt-2 border-t">
            {customModules.map(m => (
              <button key={m.id} onClick={() => go(`/cm/${m.slug}`)}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium hover:bg-muted/50">
                <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-slate-700">
                  <Boxes className="w-4 h-4 text-white" strokeWidth={1.8} />
                </span>
                {m.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
